import React, { useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader, BrowserQRCodeReader } from '@zxing/browser'
import * as ZXingBrowser from '@zxing/browser'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { LoggedInPageLayout } from '../layout/LoggedInLayout'
import { isValidIMEI } from '@/common/validators'
import './scanner.css'
import { BackButton } from '../layout/NavBar'

interface QRScannerProps {
  services: Services
  onScan: (data: string) => void
}

interface SystemVideoAPIRef {
  stream?: MediaStream
  controls: { 
    stop: () => void 
  }
}

export const QRScanner: React.FC<QRScannerProps> = ({ onScan }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const systemRef = useRef<SystemVideoAPIRef | null>(null)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const permErrorMsg = 'Camera access denied. Please allow camera access in your browser settings.' 

  useEffect(() => {
    let cancelled = false

    const startScan = async () => {
      try {
        setIsLoading(true)
        setCameraError(null)
        setPermissionDenied(false)

        const codeReader = new ZXingBrowser.BrowserQRCodeReader()
        const videoInputDevices = await ZXingBrowser.BrowserCodeReader.listVideoInputDevices()

        // Try to select a back camera
        let selectedDevice = videoInputDevices.find(
          (d) => d.label && d.label.toLowerCase().includes('back')
        )
        // Fallback: pick the second device (often back on iOS!)
        if (!selectedDevice && videoInputDevices.length > 1) {
          selectedDevice = videoInputDevices[1]
        }
        // Otherwise, use first device
        if (!selectedDevice && videoInputDevices.length > 0) {
          selectedDevice = videoInputDevices[0]
        }

        if (!selectedDevice || !videoRef.current) throw new Error('No camera device found')

        const controls = await codeReader.decodeFromVideoDevice(
          selectedDevice.deviceId,
          videoRef.current,
          (result: any, err: any, controls: any) => {
            if (cancelled) {
              controls.stop()
              return
            }
            setIsLoading(false)
            if (result) {
              // controls.stop() // Uncomment to stop after first successful scan
              onScan(result.getText())
            }
          }
        )
        systemRef.current = {
          // stream,
          controls
        }
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'name' in error &&
          (
            (error as any).name === 'NotAllowedError' ||
            (error as any).name === 'PermissionDeniedError'
          )
        ) {
          setPermissionDenied(true)
        } else if (
          typeof error === 'object' &&
          error !== null &&
          'name' in error &&
          (error as any).name === 'NotReadableError'
        ) {
          setCameraError(
            'Could not access the camera. On Android: ensure no other app or tab is using the camera, try using Google Chrome, grant camera permission, and try again after tapping "Start QR Scanner".'
          )
        } else {
          setCameraError('Unknown error initializing camera: ' + (error as any)?.message)
        }
        setIsLoading(false)
      }
    }

    startScan()
    
    return () => {
      cancelled = true
      if(systemRef.current) {
        const stream = systemRef.current.stream
        const constrols = systemRef.current.controls
        if(constrols) {
          constrols.stop()
        }
        if(stream) {
          stream.getTracks().forEach((track) => track.stop())
        }
        systemRef.current = null
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }, [onScan])

  // UI: Only start scanner after click
  // if (!started)
  //   return (
  //     <div className="qr-scanner">
  //       <button onClick={() => setStarted(true)}>Start QR Scanner</button>
  //       {permissionDenied && <div className='camera-error'>{permErrorMsg}</div>}
  //       {cameraError && <div className="camera-error">{cameraError}</div>}
  //     </div>
  //   )

  return (
    <div className='qr-scanner'>
      {isLoading ? <div className='spinner'></div> : null}
      {permissionDenied && <div className='camera-error'>{permErrorMsg}</div>}
      {cameraError && <div className="camera-error">{cameraError}</div>}
      <video
        ref={videoRef}
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        muted
      />
    </div>
  )
}

export const QRScannerPage: React.FC<{ session: CurrentSession, services: Services }> = ({ services, session }) => {
  const [scannerData, setScannerData] = useState<string>()

  return (
    <LoggedInPageLayout pageName='qr-scanner' services={services} session={session}
      navbarLeft={<BackButton services={services} translates={session.translates} />}>
      <div>
        <h1>QR Code Scanner</h1>
        <QRScanner
          onScan={(data: string) => setScannerData(data)}
          services={services}
        />
        {scannerData
          ? isValidIMEI(scannerData)
            ? scannerData
            : `${scannerData} is not correct imei`
          : null}
      </div>
    </LoggedInPageLayout>
  )
}